import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { Space, Table, Tag, Tooltip } from 'antd';
import tinycolor from 'tinycolor2';
import { CHANNEL_META } from '../Reminders/channelMeta';
import AccountCell from '../Reminders/AccountCell';
import { getAssigneAccountCellProps, getCreatorAccountCellProps } from '../Reminders/accountCellHelper';
import { STATE_LABELS } from '../../views/Reminders/stateLabels';
import { ACTIVITY_COLORS, ACTIVITY_LABELS } from '../../views/Reminders/constants';

interface AllRemindersTabProps {
  loading: boolean;
  dataSource: any[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize?: number) => void;
  emptyText?: string;
}

const STATE_COLOR_MAP: Record<string, string> = {
  PENDING: 'orange',
  PROCESSING: 'cyan',
  ACCEPT: 'green',
  REJECT: 'red',
  COMPLETED: 'blue',
  CLOSED: 'gray',
};

const ALL_REMINDER_TYPE_SET = new Set(['STATUS_CHANGE', 'RESPONSIBLE_CHANGE', 'NOTE']);

const ALL_TYPE_COLORS: Record<string, string> = {
  STATUS_CHANGE: 'blue',
  RESPONSIBLE_CHANGE: 'orange',
  NOTE: 'purple',
  ...(ACTIVITY_COLORS as Record<string, string>),
};

const parseReminderProperties = (value?: Record<string, any> | string | null) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error('İşlem geçmişi property parsing failed:', error);
      return {};
    }
  }
  if (typeof value === 'object') {
    return value;
  }
  return {};
};

const getNameFromValue = (value: any) => {
  if (!value) return undefined;
  if (typeof value === 'string') {
    return value.trim() || undefined;
  }
  if (typeof value === 'object') {
    const firstLast = `${value.firstName ?? ''} ${value.lastName ?? ''}`.trim();
    if (firstLast) return firstLast;
    if (value.fullName) return value.fullName;
    if (value.name) return value.name;
    if (value.email) return value.email;
  }
  return undefined;
};

const collectAssigneeNames = (props: Record<string, any>) => {
  const names = new Set<string>();
  Object.entries(props).forEach(([key, value]) => {
    if (!value) return;
    const normalizedKey = key.toLowerCase();
    if (normalizedKey.includes('assignee') || normalizedKey.includes('responsible')) {
      const extracted = getNameFromValue(value);
      if (extracted) {
        names.add(extracted);
        return;
      }
    }
    if (normalizedKey.includes('user') || normalizedKey.includes('account')) {
      const extracted = getNameFromValue(value);
      if (extracted) {
        names.add(extracted);
      }
    }
  });
  return Array.from(names);
};

const getResponsibleChangeLabel = (record: any) => {
  const props = parseReminderProperties(record.properties);
  const names = collectAssigneeNames(props);
  if (names.length > 0) {
    return names.join(', ');
  }
  return record.content || '-';
};

const getActivityTypeKey = (record: any) =>
  record?.activityType ? String(record.activityType).toUpperCase() : '';

const getActivityTypeLabel = (record: any) => {
  const key = getActivityTypeKey(record);
  if (key === 'STATUS_CHANGE') return 'Statü Değişikliği';
  if (key === 'RESPONSIBLE_CHANGE') return 'Sorumlu Değişikliği';
  if (key === 'NOTE') return 'Not';
  if (!key) return 'Görev';
  return (ACTIVITY_LABELS as Record<string, string>)[key] || key;
};

const renderReminderStateTag = (state?: string) => {
  if (!state) return null;
  const label = (STATE_LABELS as Record<string, string>)[state] || state;
  return <Tag color={STATE_COLOR_MAP[state] || 'default'}>{label}</Tag>;
};

const renderReminderStatusBadge = (status?: any) => {
  const statusName = status?.name;
  if (!statusName) return null;
  const baseColor = status?.color || '#d1d5db';
  const color = tinycolor(baseColor);
  const transparentColor = color.setAlpha(0.1).toRgbString();
  const transparentBorder = color.setAlpha(0.5).toRgbString();

  return (
    <Tag
      style={{
        color: baseColor,
        backgroundColor: transparentColor,
        borderColor: transparentBorder,
        borderRadius: '16px',
        padding: '4px 10px',
        fontSize: '13px',
        display: 'inline-block',
        minWidth: '100px',
        textAlign: 'center',
        fontWeight: 600,
      }}
    >
      {statusName}
    </Tag>
  );
};

const renderReminderChannelIcon = (channel?: string) => {
  if (!channel) return null;
  const meta = (CHANNEL_META as Record<string, any>)[channel];
  if (!meta) return null;
  return (
    <Tooltip title={meta.label}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          fontSize: 16,
          color: '#374151',
        }}
      >
        {meta.icon}
      </span>
    </Tooltip>
  );
};

const getReminderResultValue = (record: any) => {
  if (record?.result) return record.result;
  const raw = record?.properties;
  if (!raw) return undefined;
  if (typeof raw === 'object') return raw?.result;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed?.result;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const renderTaskContent = (record: any) => {
  const resultValue = getReminderResultValue(record);
  const statusTag = renderReminderStatusBadge(record?.status);
  const channelIcon = renderReminderChannelIcon(record?.channel);
  return (
    <div>
      {resultValue && (
        <div style={{ color: '#374151', fontSize: 13, marginBottom: 4 }}>
          <b>Sonuç:</b> {resultValue}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        {channelIcon}
        <Tooltip title={record?.id ? `#${record.id}` : undefined}>
          <span>{record?.content || 'Görev içeriği bulunamadı.'}</span>
        </Tooltip>
      </div>
      {statusTag && <div style={{ marginTop: 6 }}>{statusTag}</div>}
    </div>
  );
};

const renderAllDetail = (record: any) => {
  const key = getActivityTypeKey(record);
  if (key === 'STATUS_CHANGE') {
    const statusTag = renderReminderStatusBadge(record?.status);
    if (statusTag) return statusTag;
    if (record?.statusId) return `#${record.statusId}`;
    return record?.content || '-';
  }
  if (key === 'RESPONSIBLE_CHANGE') {
    return getResponsibleChangeLabel(record);
  }
  if (key === 'NOTE') {
    return record?.content || '-';
  }
  return renderTaskContent(record);
};

const renderAllAccountCell = (record: any) => {
  const key = getActivityTypeKey(record);
  const useCreator = key === 'STATUS_CHANGE' || key === 'RESPONSIBLE_CHANGE' || key === 'NOTE';
  return (
    <AccountCell
      {...(useCreator ? getCreatorAccountCellProps(record) : getAssigneAccountCellProps(record))}
    />
  );
};

const getReminderSortValue = (record: any) => {
  const rawDate = record?.reminderDate;
  if (!rawDate) return 0;
  const parsed = dayjs(rawDate);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

const AllRemindersTab: React.FC<AllRemindersTabProps> = ({
  loading,
  dataSource,
  page,
  pageSize,
  total,
  onPageChange,
  emptyText = 'Henüz işlem bulunmuyor.',
}) => {
  const sortedData = useMemo(() => {
    const next = Array.isArray(dataSource) ? [...dataSource] : [];
    next.sort((a, b) => getReminderSortValue(b) - getReminderSortValue(a));
    return next;
  }, [dataSource]);

  const columns = useMemo(
    () => [
      {
        title: 'Tür',
        key: 'type',
        render: (_: any, record: any) => {
          const key = getActivityTypeKey(record);
          const label = getActivityTypeLabel(record);
          const color = ALL_TYPE_COLORS[key] || 'default';
          return <Tag color={color}>{label}</Tag>;
        },
      },
      {
        title: 'Detay',
        width: 400,
        key: 'detail',
        render: (_: any, record: any) => renderAllDetail(record),
      },
      {
        title: 'Kullanıcı',
        key: 'account',
        render: (_: any, record: any) => renderAllAccountCell(record),
      },
      {
        title: 'Tarih',
        key: 'reminderDate',
        render: (_: any, record: any) => {
          const value = record?.reminderDate;
          return value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-';
        },
      },
      {
        title: 'Durum',
        key: 'state',
        render: (_: any, record: any) => {
          const activityKey = getActivityTypeKey(record);
          if (ALL_REMINDER_TYPE_SET.has(activityKey)) {
            return '-';
          }
          const stateTag = renderReminderStateTag(record?.state);
          if (!stateTag) return '-';
          return (
            <Space direction="vertical" size={2}>
              {stateTag}
            </Space>
          );
        },
      },
    ],
    []
  );

  return (
    <Table
      loading={loading}
      dataSource={sortedData}
      columns={columns as any}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        onChange: onPageChange,
      }}
      rowKey={(record) =>
        record?.id ??
        `${record?.entityId}-${record?.reminderDate || record?.createdAt || Math.random().toString()}`
      }
      locale={{ emptyText }}
      scroll={{ x: 'max-content' }}
    />
  );
};

export default AllRemindersTab;
